<template>
  <themed-card>
    <template #header>
      <h2 class="text-xl font-semibold">Billing Information</h2>
    </template>

    <form @submit.prevent="handleSubmit">
      <!-- Loading -->
      <div v-if="isLoading" class="text-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p class="mt-2 text-muted-foreground">Loading billing information...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="billing-error">
        <div class="flex">
          <div class="flex-shrink-0">
            <theme-icon icon="alert-circle" type="icons" />
          </div>
          <div class="ml-3">
            <p class="text-sm">{{ error }}</p>
            <p v-if="stripeError" class="text-xs mt-1">{{ stripeError }}</p>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Contact Info -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <themed-form-field label="Name" required>
            <themed-input
              v-model="formData.name"
              type="text"
              required
              :disabled="isSubmitting"
            />
          </themed-form-field>

          <themed-form-field label="Email" required>
            <themed-input
              v-model="formData.email"
              type="email"
              required
              :disabled="isSubmitting"
            />
          </themed-form-field>
        </div>

        <!-- Stripe Address Element -->
        <div class="billing-address-section">
          <h3 class="text-lg font-medium mb-4">Billing Address</h3>

          <div v-if="isStripeReady">
            <div ref="stripeAddressEl" class="billing-stripe-address" :key="themeKey"></div>
            <p v-if="addressError" class="billing-address-error">{{ addressError }}</p>
          </div>
          <div v-else>
            <p class="text-sm text-muted-foreground">Loading address form...</p>
          </div>
        </div>

        <!-- Submit -->
        <div class="billing-actions">
          <themed-button
            type="submit"
            :disabled="isSubmitting || isLoading || !addressElement"
          >
            <span v-if="isSubmitting">
              <theme-icon icon="spinner" type="icons" class="animate-spin" />
              Saving...
            </span>
            <span v-else>Save Changes</span>
          </themed-button>
        </div>
      </div>
    </form>
  </themed-card>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import { mapActions } from 'vuex';
import ThemedCard from '../ThemedCard.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedFormField from '../form/ThemedFormField.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'BillingInformation',
  components: {
    ThemedCard,
    ThemedButton,
    ThemedInput,
    ThemedFormField,
    ThemeIcon
  },
  created() {
    // Vue does not proxy keys beginning with an underscore, so these were
    // never reactive state. They are plain instance properties.
    this._addressReady = false;
    this._prefillAddress = null;
  },
  data() {
    return {
      formData: { name: '', email: '', phone: '' },
      isLoading: true,
      isSubmitting: false,
      error: null,
      addressError: null,
      stripeError: null,
      isStripeReady: false,
      stripe: null,
      elements: null,
      addressElement: null,
      themeKey: 0,
      themeObserver: null,
    };
  },
  async mounted() {
    try {
      this.isLoading = true;
      this.stripe = await loadStripe(window.STRIPE_PUBLISHABLE_KEY);
      if (!this.stripe) throw new Error('Failed to initialize Stripe');

      await this.loadBillingInfo();
      this.isStripeReady = true;
      
      // Watch for theme changes
      this.setupThemeObserver();
    } catch (err) {
      console.error('Error during mounted:', err);
      this.stripeError = err?.message || 'Stripe init failed';
      this.error = 'Failed to initialize payment form. Please refresh and try again.';
    } finally {
      this.isLoading = false;
      await this.$nextTick();
      if (!this.error) {
        try {
          await this.initializeStripeElements();
        } catch (err) {
          this.stripeError = err?.message || 'Unable to render address form';
        }
      }
    }
  },
  beforeUnmount() {
    try {
      this.addressElement?.unmount?.();
      this.addressElement = null;
      this.elements = null;
      
      // Disconnect theme observer
      if (this.themeObserver) {
        this.themeObserver.disconnect();
        this.themeObserver = null;
      }
    } catch {
      // Teardown must not throw: the element may already be unmounted.
    }
  },
  methods: {
    ...mapActions('billing', {
      fetchBillingInfo: 'fetchBillingInfo',
      updateBillingInfo: 'updateBillingInfo'
    }),
    setupThemeObserver() {
      // Watch for class changes on html element (dark mode toggle)
      this.themeObserver = new MutationObserver(() => {
        this.recreateStripeElement();
      });
      
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });
    },
    async recreateStripeElement() {
      try {
        // Unmount existing element
        if (this.addressElement) {
          this.addressElement.unmount();
          this.addressElement = null;
        }
        
        // Force re-render with new theme
        this.themeKey++;
        await this.$nextTick();
        
        // Recreate with current theme colors
        await this.initializeStripeElements();
      } catch (err) {
        console.error('Error recreating Stripe element:', err);
      }
    },
    getStripeAppearance() {
      const rootStyles = getComputedStyle(document.documentElement);
      
      // Get colors based on current theme
      const inputBg = rootStyles.getPropertyValue('--input-background').trim();
      const textColor = rootStyles.getPropertyValue('--foreground').trim();
      const borderColor = rootStyles.getPropertyValue('--border').trim();
      const primaryColor = rootStyles.getPropertyValue('--primary').trim();
      const mutedForeground = rootStyles.getPropertyValue('--muted-foreground').trim();
      
      return {
        theme: 'none',
        variables: {
          colorBackground: inputBg,
          colorText: textColor,
          colorPrimary: primaryColor,
          colorDanger: '#ef4444',
          fontFamily: 'Inter, system-ui, sans-serif',
          borderRadius: '0.5rem',
          spacingUnit: '4px',
        },
        rules: {
          '.Input': {
            backgroundColor: inputBg,
            border: `1px solid ${borderColor}`,
            color: textColor,
            padding: '0.625rem 0.75rem',
            fontSize: '0.875rem',
            boxShadow: 'none',
          },
          '.Input:focus': {
            borderColor: primaryColor,
            boxShadow: `0 0 0 1px ${primaryColor}`,
            outline: 'none',
          },
          '.Input::placeholder': {
            color: mutedForeground,
          },
          '.Label': {
            color: textColor,
            fontWeight: '500',
            fontSize: '0.875rem',
            marginBottom: '0.5rem',
          },
          '.Error': {
            color: '#ef4444',
            fontSize: '0.875rem',
          },
        },
      };
    },
    async loadBillingInfo() {
      const api = await this.fetchBillingInfo();
      const payload = api?.data;
      if (!payload) return;

      this.formData = {
        name:  payload.name  ?? '',
        email: payload.email ?? '',
        phone: payload.phone ?? '',
      };

      const a = payload.address ?? {};
      this._prefillAddress = {
        name: this.formData.name || '',
        phone: this.formData.phone || '',
        address: {
          line1: a.line1 || '',
          line2: a.line2 || '',
          city: a.city || '',
          state: a.state || '',
          postal_code: a.postal_code || a.postalCode || '',
          country: a.country || 'US',
        },
      };

      if (this.addressElement) {
        this.updateAddressElement();
      }
    },
    async initializeStripeElements() {
      if (!this.$refs.stripeAddressEl) {
        await this.$nextTick();
      }

      // Get appearance based on current theme
      const appearance = this.getStripeAppearance();
      
      this.elements = this.stripe.elements({ appearance });

      this.addressElement = this.elements.create('address', {
        mode: 'billing',
        fields: { phone: 'always' },
        defaultValues: this._prefillAddress || {},
      });

      this.addressElement.on('ready', () => { this._addressReady = true; });
      this.addressElement.on('change', (event) => {
        this.addressError = event.error?.message || null;
      });

      this.addressElement.mount(this.$refs.stripeAddressEl);
    },
    async updateAddressElement() {
      if (!this.addressElement || !this._prefillAddress) return;

      const addressData = {
        name: this._prefillAddress.name || '',
        phone: this.formatPhoneNumber(this._prefillAddress.phone || ''),
        address: {
          line1: this._prefillAddress.address?.line1 ?? '',
          line2: this._prefillAddress.address?.line2 ?? '',
          city: this._prefillAddress.address?.city ?? '',
          state: this._prefillAddress.address?.state ?? '',
          postal_code: this._prefillAddress.address?.postal_code ?? '',
          country: this._prefillAddress.address?.country ?? 'US',
        },
      };

      if (!this._addressReady) {
        await new Promise((resolve) => {
          this.addressElement.on('ready', () => {
            this._addressReady = true;
            resolve();
          });
        });
      }

      this.addressElement.update({ defaultValues: addressData });
    },
    async handleSubmit() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      this.error = null;
      this.addressError = null;

      try {
        if (!this.addressElement) {
          throw new Error('Address form not ready. Please try refreshing the page.');
        }

        const addressValue = await this.addressElement.getValue();
        if (!addressValue || !addressValue.complete) {
          this.addressError = 'Please complete all required address fields.';
          return;
        }

        const address = addressValue.value?.address || addressValue.address || {};
        const billingData = {
          name: this.formData.name?.trim() || '',
          email: this.formData.email?.trim() || '',
          phone: this.formData.phone?.trim() || '',
          address: {
            line1: address.line1?.trim() || '',
            line2: address.line2?.trim() || '',
            city: address.city?.trim() || '',
            state: address.state?.trim() || '',
            postal_code: address.postal_code?.trim() || '',
            country: address.country?.trim() || '',
          }
        };

        await this.updateBillingInfo(billingData);
        this.$toast?.success?.('Billing information updated successfully');
      } catch (err) {
        console.error('Error updating billing info:', err);
        this.error = err?.response?.data?.message || err?.message || 'Failed to update billing information. Please try again.';
      } finally {
        this.isSubmitting = false;
      }
    },
    formatPhoneNumber(phone) {
      if (!phone) return '';
      const cleaned = ('' + phone).replace(/\D/g, '');
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
    },
  },
};
</script>
